import { useNavigate } from "react-router-dom";
import { ArrowLeft, Globe, Heart, Shield, Users, Scale, Eye, Sparkles, PawPrint, ExternalLink } from "lucide-react";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel } from "@/styles/editorial";

const UNESCO_PILLARS = [
  {
    num: "01", title: "Proporcionalidad y no causar daño",
    icon: Shield, color: C.red,
    description: "Los sistemas de IA deben ser proporcionados y no perjudicar a las personas ni al medio ambiente.",
    ourCommitment: "Usamos IA solo donde aporta valor real: no procesamos datos personales con IA, no generamos contenido automatizado sin supervisión, y nuestros algoritmos priorizan siempre la vida sobre la eficiencia.",
  },
  {
    num: "02", title: "Seguridad y protección",
    icon: Eye, color: C.blue,
    description: "Los sistemas de IA deben ser seguros, protegidos y resistentes durante todo su ciclo de vida.",
    ourCommitment: "Toda IA que implementemos pasa por revisión humana. No desplegamos modelos sin pruebas exhaustivas. Priorizamos la seguridad de las personas y los animales sobre la automatización.",
  },
  {
    num: "03", title: "Equidad y no discriminación",
    icon: Users, color: C.lilac,
    description: "Los sistemas de IA deben ser justos e inclusivos, sin discriminar a ningún grupo.",
    ourCommitment: "Nuestra plataforma es gratuita y accesible. El modo calma garantiza accesibilidad para personas neurodivergentes. No discriminamos por país, idioma ni nivel tecnológico.",
  },
  {
    num: "04", title: "Transparencia y explicabilidad",
    icon: Scale, color: C.olive,
    description: "Los sistemas de IA deben ser transparentes y explicables en la medida de lo posible.",
    ourCommitment: "Si implementamos IA, lo comunicamos. Nuestra política de datos es pública y legible. No usamos cajas negras: cada decisión automatizada es auditable y reversible.",
  },
  {
    num: "05", title: "Derechos Animales y Dignidad Animal",
    icon: PawPrint, color: C.pink,
    isOurs: true,
    description: "Respeto, protección y promoción de los derechos animales, la agencia y la dignidad animal.",
    ourCommitment: "Este es nuestro pilar propio. Ningún sistema de IA en esta plataforma tratará a los animales como objetos o datos. Los animales son seres sintientes con derechos. La tecnología que usamos está al servicio de su bienestar, nunca al revés.",
    extraPoints: [
      "La IA no sustituye el juicio veterinario ni el cuidado humano directo.",
      "No usamos IA para clasificar, priorizar o descartar animales por su valor económico.",
      "Los datos de fauna sirven exclusivamente para su protección y rescate.",
      "Promovemos una relación ética entre tecnología y vida animal.",
    ],
  },
];

export default function EticaIAPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: C.white, fontFamily: BODY }} data-testid="etica-ia-page">
      <header style={{ background: C.navy, borderBottom: `2px solid ${C.lilac}` }}>
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-semibold text-sm" style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}>
            <ArrowLeft size={18} /> Volver
          </button>
          <div className="text-center">
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: DISPLAY, color: "#fff" }}>
              {"Ética de la IA"}
            </h1>
            <p className="text-[10px] uppercase tracking-widest" style={{ fontFamily: MONO, color: C.lilac }}>
              Posicionamiento de Emergencia Animal
            </p>
          </div>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="mb-8 p-6 text-center" style={{ borderLeft: `3px solid ${C.lilac}`, background: `${C.lilac}06` }}>
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ background: C.lilac, borderRadius: 4 }}>
            <Sparkles size={28} style={{ color: "#fff" }} />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: DISPLAY, color: C.navy }}>
            Tecnología al servicio de la vida
          </h2>
          <p className="text-sm max-w-xl mx-auto mb-4" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>
            En Emergencia Animal nos adherimos a las recomendaciones de la UNESCO sobre la ética de la
            inteligencia artificial, y añadimos un quinto pilar propio: los derechos y la dignidad animal.
          </p>
          <a href="https://unesdoc.unesco.org/ark:/48223/pf0000381137_spa"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold tracking-wider transition-all hover:-translate-y-0.5"
            style={{ fontFamily: MONO, background: C.navy, color: C.lilac, borderRadius: 4 }}
            data-testid="unesco-link">
            <Globe size={13} /> UNESCO: Recomendación sobre ética de la IA
            <ExternalLink size={12} />
          </a>
        </div>

        {/* Pillars */}
        <div className="space-y-6">
          {UNESCO_PILLARS.map((pillar, i) => (
            <div key={i}
              style={{
                borderLeft: `3px solid ${pillar.color}`,
                background: pillar.isOurs ? `${pillar.color}06` : C.white,
              }}
              data-testid={`pillar-${i}`}>
              {/* Pillar Header */}
              <div className="flex items-start gap-4 p-5" style={{ borderBottom: `1px solid ${pillar.color}20` }}>
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 flex items-center justify-center" style={{ background: pillar.color, borderRadius: 4 }}>
                    <pillar.icon size={22} style={{ color: "#fff" }} />
                  </div>
                  <p className="text-center mt-1 text-[10px] font-semibold" style={{ fontFamily: MONO, color: pillar.color }}>
                    {pillar.isOurs ? "NUESTRO" : "UNESCO"}
                  </p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-bold" style={{ fontFamily: MONO, color: pillar.color }}>{pillar.num}</span>
                    {pillar.isOurs && (
                      <span className="text-[10px] font-semibold px-2 py-0.5" style={{ fontFamily: MONO, background: pillar.color, color: "#fff", borderRadius: 2 }}>
                        5.o PILAR PROPIO
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ fontFamily: DISPLAY, color: C.navy }}>
                    {pillar.title}
                  </h3>
                  <p className="text-sm" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.6 }}>
                    {pillar.description}
                  </p>
                </div>
              </div>

              {/* Our Commitment */}
              <div className="p-5">
                <p className="text-[10px] font-semibold tracking-widest mb-2" style={{ fontFamily: MONO, color: pillar.color }}>
                  NUESTRO COMPROMISO
                </p>
                <p className="text-sm" style={{ fontFamily: BODY, color: C.navy, lineHeight: 1.7 }}>
                  {pillar.ourCommitment}
                </p>

                {pillar.extraPoints && (
                  <ul className="mt-4 space-y-2">
                    {pillar.extraPoints.map((point, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm" style={{ color: C.navy }}>
                        <PawPrint size={14} className="mt-0.5 flex-shrink-0" style={{ color: pillar.color }} />
                        <span style={{ lineHeight: 1.5 }}>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-8 p-6 text-center" style={{ background: C.navy, borderRadius: 4 }}>
          <p className="text-sm text-white mb-3" style={{ fontFamily: BODY, lineHeight: 1.7 }}>
            La inteligencia artificial es una herramienta poderosa. En nuestras manos está decidir
            si la ponemos al servicio de la vida o de los datos.
          </p>
          <p className="text-lg font-bold text-white" style={{ fontFamily: DISPLAY }}>
            Nosotros elegimos la vida.
          </p>
          <p className="text-[10px] tracking-widest mt-3" style={{ fontFamily: MONO, color: C.lilac }}>
            EMERGENCIA ANIMAL · NADIE SE QUEDA ATRÁS
          </p>
        </div>
      </main>
    </div>
  );
}
